// Headless logic behind the customer portal's Spend page: periods, how a breakdown is shaped for
// reading (sorted, shares, top-N), the chart's axis, and the CSV. No rendering and no browser globals,
// so a future native shell shares it. The figures themselves are never computed here: totals, drill
// URLs and the drill gate all arrive from the server (05_views_module_update_spec.md 9b); this only
// arranges what came back.

import type {
  CustomerSpendBreakdownRow,
  CustomerSpendReport,
  CustomerSpendSeriesPoint,
} from "../data/customer-api-types";

// ------------------------------------------------------------- periods ---

export type SpendPeriodId = "3m" | "12m" | "ytd";

export const SPEND_PERIODS: { id: SpendPeriodId; label: string }[] = [
  { id: "3m", label: "3 months" },
  { id: "12m", label: "12 months" },
  { id: "ytd", label: "This year" },
];

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `date` moved back `months` whole months, clamped to the target month's last day (31 May - 3 months is 28 Feb). */
function monthsBefore(date: Date, months: number): Date {
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - months, 1),
  );
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(date.getUTCDate(), lastDay));
  return target;
}

/** The first day (YYYY-MM-DD) a period covers. Each period runs to today. */
export function spendPeriodFromDate(id: SpendPeriodId, now: Date): string {
  if (id === "ytd") return `${now.getUTCFullYear()}-01-01`;
  return isoDay(monthsBefore(now, id === "3m" ? 3 : 12));
}

const LONG_DATE = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** "14 Oct 2025": the period's start as the customer reads it. */
export function formatSpendDate(isoDate: string): string {
  return LONG_DATE.format(new Date(`${isoDate}T00:00:00Z`));
}

// ---------------------------------------------------------- breakdowns ---

export type SpendBreakdownKind = "site" | "product" | "person";

export interface SpendBreakdownLine {
  key: string;
  /** What the row is called; for a site, the name when the server sent one. */
  name: string;
  /** The account code shown beside a site's name; null when the name already is the code, or for any other kind. */
  code: string | null;
  total: number;
  /** 0 to 1: this row's part of the breakdown's total. */
  share: number;
  /** Bar length, 0 to 1: this row against the largest, so the small rows stay readable. */
  barFraction: number;
  /** Present only when the server said the row can be opened; its presence is the whole drill decision. */
  drillUrl: string | null;
  /** The "Not recorded" row of a breakdown by person. */
  isUnrecorded: boolean;
}

export interface SpendBreakdownView {
  lines: SpendBreakdownLine[];
  total: number;
  /** Some rows open to orders and some do not: the only case where a per-row marker earns its place. */
  isMixedDrill: boolean;
  /** Rows the server did not offer a drill URL for. */
  withheldCount: number;
}

const NOT_RECORDED = "Not recorded";

/** A breakdown by person where nobody is named: nothing to tell apart, so the page leaves it out. */
export function isOnlyUnrecorded(rows: CustomerSpendBreakdownRow[]): boolean {
  return rows.every((r) => r.key === "" || r.label === NOT_RECORDED);
}

export function shapeBreakdown(
  kind: SpendBreakdownKind,
  rows: CustomerSpendBreakdownRow[],
): SpendBreakdownView {
  const sorted = [...rows]
    .map((r) => ({ row: r, total: Number(r.total) }))
    .sort((a, b) => b.total - a.total);
  const total = sorted.reduce((sum, r) => sum + r.total, 0);
  const largest = sorted[0]?.total ?? 0;
  const lines = sorted.map(({ row, total: rowTotal }): SpendBreakdownLine => {
    const hasName = kind === "site" && row.label !== row.key && row.key !== "";
    return {
      key: row.key,
      name: row.label,
      code: hasName ? row.key : null,
      total: rowTotal,
      share: total > 0 ? rowTotal / total : 0,
      barFraction: largest > 0 ? Math.max(rowTotal, 0) / largest : 0,
      drillUrl: row.drill_url ?? null,
      isUnrecorded:
        kind === "person" && (row.key === "" || row.label === NOT_RECORDED),
    };
  });
  const withheldCount = lines.filter((l) => l.drillUrl === null).length;
  return {
    lines,
    total,
    isMixedDrill: withheldCount > 0 && withheldCount < lines.length,
    withheldCount,
  };
}

/** How many rows show before "Show all": enough to read the leaders, few enough that 55 sites stay scannable. */
export const BREAKDOWN_PREVIEW_ROWS = 8;
/** At or below this many rows, everything shows: hiding two rows behind a button is worse than showing them. */
const BREAKDOWN_SHOW_ALL_THRESHOLD = 10;

export interface SpendBreakdownWindow {
  shown: SpendBreakdownLine[];
  /** Rows folded into "other" while the list is collapsed; 0 when everything shows. */
  hiddenCount: number;
  hiddenTotal: number;
  hiddenShare: number;
  canCollapse: boolean;
}

export function windowBreakdown(
  view: SpendBreakdownView,
  expanded: boolean,
): SpendBreakdownWindow {
  const needsWindow = view.lines.length > BREAKDOWN_SHOW_ALL_THRESHOLD;
  if (!needsWindow || expanded) {
    return {
      shown: view.lines,
      hiddenCount: 0,
      hiddenTotal: 0,
      hiddenShare: 0,
      canCollapse: needsWindow,
    };
  }
  const shown = view.lines.slice(0, BREAKDOWN_PREVIEW_ROWS);
  const hidden = view.lines.slice(BREAKDOWN_PREVIEW_ROWS);
  const hiddenTotal = hidden.reduce((sum, l) => sum + l.total, 0);
  return {
    shown,
    hiddenCount: hidden.length,
    hiddenTotal,
    hiddenShare: view.total > 0 ? hiddenTotal / view.total : 0,
    canCollapse: false,
  };
}

/** "3%", or "<1%" for a real but tiny share, so a small row is never shown as nothing. */
export function formatShare(share: number): string {
  if (share <= 0) return "0%";
  const percent = share * 100;
  return percent < 1 ? "<1%" : `${Math.round(percent)}%`;
}

// --------------------------------------------------------------- chart ---

export interface SpendChartBar {
  month: string;
  /** "Oct", the label under the bar. */
  shortLabel: string;
  /** "October 2025", the name a screen reader and the readout use. */
  longLabel: string;
  /** Shown under the bar on the first one and on each January: the year the months belong to. */
  yearLabel: string | null;
  total: number;
  /** 0 to 1 of the axis ceiling. */
  height: number;
}

export interface SpendChart {
  bars: SpendChartBar[];
  /** Axis labels, top to bottom, with where each sits (0 to 1 of the ceiling). */
  ticks: { value: number; fraction: number }[];
  ceiling: number;
}

const SHORT_MONTH = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  timeZone: "UTC",
});
const LONG_MONTH = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

function monthStart(month: string): Date {
  return new Date(`${month}-01T00:00:00Z`);
}

/** The next "round" number at or above `max`: 1, 2, 2.5, 5 or 10 times a power of ten, so the axis reads cleanly. */
export function niceCeiling(max: number): number {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const scaled = max / magnitude;
  const step = [1, 2, 2.5, 5, 10].find((s) => scaled <= s) ?? 10;
  return step * magnitude;
}

export function shapeChart(series: CustomerSpendSeriesPoint[]): SpendChart {
  const ordered = [...series].sort((a, b) => a.month.localeCompare(b.month));
  const ceiling = niceCeiling(
    Math.max(...ordered.map((p) => Number(p.total)), 0),
  );
  const bars = ordered.map((p, index): SpendChartBar => {
    const start = monthStart(p.month);
    const isJanuary = start.getUTCMonth() === 0;
    return {
      month: p.month,
      // en-GB abbreviates September to "Sept"; three letters keeps every label the same width.
      shortLabel: SHORT_MONTH.format(start).slice(0, 3),
      longLabel: LONG_MONTH.format(start),
      yearLabel:
        index === 0 || isJanuary ? String(start.getUTCFullYear()) : null,
      total: Number(p.total),
      height: Math.max(Number(p.total), 0) / ceiling,
    };
  });
  return {
    bars,
    ceiling,
    ticks: [
      { value: ceiling, fraction: 1 },
      { value: ceiling / 2, fraction: 0.5 },
      { value: 0, fraction: 0 },
    ],
  };
}

// ----------------------------------------------------------------- csv ---

function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The Export file: the two bases, then the site and product breakdowns. Cells are quoted, so a name with a comma stays one cell. */
export function spendReportCsv(report: CustomerSpendReport): string {
  const money = (value: string | number) => Number(value).toFixed(2);
  const rows: string[][] = [
    ["Basis", "Amount"],
    ["Invoiced to you", money(report.invoiced_total)],
    ["Ordered", money(report.committed_total)],
    [],
    ["Site", "Amount"],
    ...report.by_site.map((s) => [s.label, money(s.total)]),
    [],
    ["Product", "Amount"],
    ...report.by_product.map((p) => [p.label, money(p.total)]),
  ];
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}
